import { useEffect, useState } from "react";
import { Mail, Linkedin, GraduationCap, Fingerprint, FileText, Copy, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/SectionLabel";
import { PageMeta } from "@/components/site/PageMeta";
import { getSection, submitContact } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function ContactPage() {
    const [contact, setContact] = useState(null);
    const [copied, setCopied] = useState("");
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);

    useEffect(() => { getSection("contact").then((r) => setContact(r.data || {})); }, []);
    const c = contact || {};

    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopied(key); toast.success("Copied to clipboard"); setTimeout(() => setCopied(""), 1500); }
        catch { toast.error("Could not copy"); }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) { toast.error("Please fill in name, email, and message"); return; }
        setSending(true);
        try {
            await submitContact(form);
            toast.success("Message sent — thank you.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch { toast.error("Failed to send — please try again."); }
        finally { setSending(false); }
    };

    const cards = [
        ...(c.emails || []).map((e) => ({ key: `email-${e}`, icon: Mail, label: "Email", value: e, href: `mailto:${e}`, copy: e })),
        c.linkedin && { key: "linkedin", icon: Linkedin, label: "LinkedIn", value: c.linkedin.replace(/^https?:\/\//, ""), href: c.linkedin },
        c.google_scholar && { key: "scholar", icon: GraduationCap, label: "Google Scholar", value: c.google_scholar.replace(/^https?:\/\//, ""), href: c.google_scholar },
        c.orcid && { key: "orcid", icon: Fingerprint, label: "ORCID", value: c.orcid.replace(/^https?:\/\//, ""), href: c.orcid },
        c.ssrn && { key: "ssrn", icon: FileText, label: "SSRN", value: "papers.ssrn.com/…", href: c.ssrn },
    ].filter(Boolean);

    return (
        <>
            <PageMeta title="Contact — Aditya Baheti" description="Get in touch for collaborations, teaching invitations, and conversations." path="/contact" />

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-6">
                <SectionLabel label="Contact · Say hello" />
                <Reveal>
                    <h1 className="font-display text-4xl sm:text-6xl leading-[1.05] tracking-tight max-w-3xl">Let’s talk research, teaching, or ideas.</h1>
                </Reveal>
                <Reveal delay={0.05}>
                    <p className="mt-4 max-w-[62ch] text-base sm:text-lg text-muted-foreground">{c.intro}</p>
                </Reveal>
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7 order-2 lg:order-1">
                    <SectionLabel label="Send a message" />
                    <Card className="p-6 sm:p-8 rounded-2xl border-border/70">
                        <form onSubmit={onSubmit} className="grid gap-4" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="c-name">Name</Label>
                                    <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="c-email">Email</Label>
                                    <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email-input" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="c-subject">Subject</Label>
                                <Input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="c-message">Message</Label>
                                <Textarea id="c-message" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message-input" />
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="btn-outline-pill accent disabled:opacity-60"
                                    data-testid="contact-submit-button"
                                >
                                    {sending ? "Sending…" : "Send message"} <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </form>
                    </Card>
                </div>

                <div className="lg:col-span-5 order-1 lg:order-2">
                    <SectionLabel label="Direct channels" />
                    <address className="not-italic space-y-3">
                        {cards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <Card key={card.key} className="group p-4 rounded-2xl border-border/70 hover:border-border transition-shadow flex items-center justify-between gap-3">
                                    <a href={card.href} target={card.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer noopener" className="flex items-center gap-3 min-w-0" data-testid={`contact-${card.key}-link`}>
                                        <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center">
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{card.label}</p>
                                            <p className="text-sm truncate">{card.value}</p>
                                        </div>
                                    </a>
                                    {card.copy && (
                                        <Button variant="ghost" size="icon" onClick={() => copy(card.copy, card.key)} data-testid={`contact-${card.key}-copy-button`}>
                                            {copied === card.key ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                        </Button>
                                    )}
                                </Card>
                            );
                        })}
                    </address>
                </div>
            </section>
        </>
    );
}
